import React, { useEffect, useMemo, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from '@cokitchen/cokitchen-components';
import { ENUM_PROCUREMENT_STATUS, ProcurementType } from 'models/procurement.model';
import ProcurementStatusTag from 'components/base/procurement-status-tag';
import { apiService } from 'services/base.service';
import ProcurementItemMenu from './procurement-item-menu';
import MultipleRequestItemPill from 'components/multiple-request-item-pill';
import CompleteRequestModal from 'components/modals/complete-request-modal';
import RequestDetailsWrapper from 'components/base/request-details-wrapper';
import { EditAltIcon } from 'cokitchen-icons';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const UpdateProcurement = () => {
  const [procurement, setProcurement] = useState({} as ProcurementType);
  const [loading, setLoading] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const { activeStore } = useDashboardContext();
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useRoleContext();
  const canWriteProcurement = can(InventoryModule.Procurement, RolePermission.Write);

  const handleChange = (itemId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canWriteProcurement) return;
    const { checked } = event.target;
    setProcurement((prev) => ({
      ...prev,
      items: prev.items.map((data) =>
        data.id === itemId ? { ...data, is_complete: checked } : data
      )
    }));
    setSelectedItemIds((prev) =>
      checked ? [...prev, itemId] : prev.filter((id) => id !== itemId)
    );
  };

  const getProcurementItems = async () => {
    if (!activeStore) return;
    try {
      setLoading(true);
      const res: { data: { data: ProcurementType } } = await apiService.get(`procurement/${id}`, {
        version: 'v2'
      });
      if (res) {
        const data = res?.data?.data;
        setProcurement(data ?? ({} as ProcurementType));
        if (data?.items) {
          const initiallySelected = data.items
            .filter((item) => item.is_complete)
            .map((item) => item.id);
          setSelectedItemIds(initiallySelected);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getProcurementItems();
  }, []);

  const receiveItem = (ids: string) => {
    if (!canWriteProcurement) return;
    navigate(`/main-store/procurement/update/receive-items/${ids}`);
  };

  const closeRequest = async () => {
    if (!canWriteProcurement) return;
    setShowCompleteModal(true);
  };
  const returnItem = (ids: string) => {
    if (!canWriteProcurement) return;
    navigate(`/main-store/procurement/update/return-items/${ids}`);
  };

  const handleEdit = () => {
    if (!canWriteProcurement) return;
    navigate('/main-store/procurement/new', {
      state: {
        mode: 'edit',
        procurement
      }
    });
  };

  const pillActions = useMemo(
    () => [
      {
        label: 'Complete request',
        onClick: () => closeRequest()
      },
      {
        label: 'Return items',
        onClick: () => returnItem(selectedItemIds.join(','))
      },
      {
        label: 'Receive items',
        onClick: () => receiveItem(selectedItemIds.join(','))
      }
    ],
    [selectedItemIds]
  );

  return (
    <>
      <RequestDetailsWrapper
        codeId={procurement.code}
        statusTag={procurement?.status ? <ProcurementStatusTag type={procurement.status} /> : null}
        header='Update request'
        subHeader='Update item as received or returned once vendor delivers'
        headerExtra={
          canWriteProcurement &&
          procurement.status === ENUM_PROCUREMENT_STATUS.CANCELLED && (
            <button
              className='flex items-center  bg-red-5 text-white font-medium text-sm px-2 gap-2 py-2'
              onClick={handleEdit}>
              {' '}
              <EditAltIcon />
              Edit Request
            </button>
          )
        }>
        {loading ? (
          <div>
            <p className='flex justify-center items-center h-[300px]'>Loading Procurement Items</p>
          </div>
        ) : !procurement?.items?.length ? (
          <div className='flex justify-center items-center h-[300px]'>
            <p>No Procurement items yet</p>
          </div>
        ) : (
          <div>
            {procurement.items.map((item, index) => (
              <div key={index} className='py-[24px] px-[28px] border-b border-b-[#F7F7F8]'>
                <div className='flex justify-between'>
                  <div className='flex gap-4 items-center mr-2'>
                    {(item.status === ENUM_PROCUREMENT_STATUS.APPROVED ||
                      item.status === ENUM_PROCUREMENT_STATUS.RECEIVED) && (
                      <Input
                        type='checkbox'
                        className=''
                        checked={item.is_complete}
                        disabled={!canWriteProcurement}
                        onChange={(e) => handleChange(item.id, e)}
                      />
                    )}
                    <div>
                      <p className='font-medium text-[#2E3031] capitalize'>
                        {item?.ingredient?.name}
                      </p>
                      <div className='flex gap-1 flex-wrap items-center'>
                        <p className='text-sm font-medium text-[#2E3031]'>
                          {item.quantity} {item?.ingredient?.unit_type}
                        </p>
                        <div className='bg-[#DCDBD5] w-[5px] h-[5px] rounded-full' />

                        <p className='text-sm font-medium text-[#0080E5] '>
                          {item.quantity_received} {item?.ingredient?.unit_type} received
                        </p>

                        <div className='flex items-center gap-1'>
                          <div className='bg-[#DCDBD5] w-[5px] h-[5px] rounded-full' />
                          <p className='text-[#6F7376] text-sm font-medium'>{item?.vendor?.name}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='flex flex-col items-end gap-6'>
                    {item?.status && <ProcurementStatusTag type={item.status} />}
                  </div>
                </div>

                {![ENUM_PROCUREMENT_STATUS.COMPLETED, ENUM_PROCUREMENT_STATUS.CANCELLED].includes(
                  item.status
                ) &&
                  item.status &&
                  canWriteProcurement && (
                    <div className='mt-3 flex justify-end'>
                      <ProcurementItemMenu
                        procurement={procurement}
                        requestId={procurement.id}
                        status={item.status}
                        ids={selectedItemIds.length ? selectedItemIds.join(',') : item.id ?? ''}
                        onCompleted={(completedIds) => {
                          setProcurement((prev) => ({
                            ...prev,
                            items: prev.items.map((it) =>
                              completedIds.includes(it.id)
                                ? {
                                    ...it,
                                    completed: true,
                                    status: ENUM_PROCUREMENT_STATUS.COMPLETED
                                  }
                                : it
                            )
                          }));
                        }}
                      />
                    </div>
                  )}
              </div>
            ))}
            <div className='flex justify-end gap-4 mt-6 items-center mb-[40px] pr-[28px]'>
              <p className='text-sm text-[#6F7376]'>Total</p>
              <h4 className='text-[#2E3031] text-2xl font-semibold'>
                ₦ {Number(procurement.expected_cost).toLocaleString()}
              </h4>
            </div>
          </div>
        )}
      </RequestDetailsWrapper>

      <div className='ml-2 md:ml-8'>
        {canWriteProcurement && selectedItemIds.length > 0 && (
          <div>
            <MultipleRequestItemPill actions={pillActions} />
          </div>
        )}
      </div>
      <CompleteRequestModal
        isOpen={canWriteProcurement && showCompleteModal}
        onRequestClose={() => setShowCompleteModal(false)}
        requestId={procurement.id}
        id={selectedItemIds}
        onSuccess={(completedIds) => {
          setProcurement((prev) => ({
            ...prev,
            items: prev.items.map((it) =>
              completedIds.includes(it.id) ? { ...it, is_complete: true } : it
            )
          }));
        }}
      />
    </>
  );
};

export default UpdateProcurement;
